import React, { useState } from 'react';
import { useUser } from '../../context/UserContext';
import { CheckIcon, XIcon, StarIcon } from 'lucide-react';
interface SchwartzValuesTestProps {
  onContinue: () => void;
}
const SchwartzValuesTest: React.FC<SchwartzValuesTestProps> = ({
  onContinue
}) => {
  const {
    updateUserData
  } = useUser();
  const [step, setStep] = useState(0);
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [valueDescriptions, setValueDescriptions] = useState<Record<string, string>>({});
  // Schwartz principles with descriptions
  const valueGroups = [{
    title: 'What matters most to you?',
    values: [{
      id: 'benevolence',
      name: 'Kindness',
      description: 'Benevolence: Preserving and enhancing the welfare of those with whom one is in frequent personal contact.'
    }, {
      id: 'universalism',
      name: 'Understanding',
      description: 'Universalism: Understanding, appreciation, tolerance, and protection for the welfare of all people and for nature.'
    }, {
      id: 'self-direction',
      name: 'Independence',
      description: 'Self-Direction: Independent thought and action—choosing, creating, exploring.'
    }, {
      id: 'stimulation',
      name: 'Excitement',
      description: 'Stimulation: Excitement, novelty, and challenge in life.'
    }]
  }, {
    title: 'Which principles guide your life?',
    values: [{
      id: 'achievement',
      name: 'Success',
      description: 'Achievement: Personal success through demonstrating competence according to social standards.'
    }, {
      id: 'security',
      name: 'Safety',
      description: 'Security: Safety, harmony, and stability of society, of relationships, and of self.'
    }, {
      id: 'conformity',
      name: 'Harmony',
      description: 'Conformity: Restraint of actions, inclinations, and impulses likely to upset or harm others and violate social expectations or norms.'
    }, {
      id: 'tradition',
      name: 'Respect',
      description: "Tradition: Respect, commitment, and acceptance of the customs and ideas that one's culture or religion provides."
    }]
  }, {
    title: 'What drives your decisions?',
    values: [{
      id: 'hedonism',
      name: 'Enjoyment',
      description: 'Hedonism: Pleasure or sensuous gratification for oneself.'
    }, {
      id: 'power',
      name: 'Influence',
      description: 'Power: Social status and prestige, control or dominance over people and resources.'
    }, {
      id: 'spirituality',
      name: 'Connection',
      description: 'Spirituality: Finding meaning and inner harmony through transcending everyday reality.'
    }, {
      id: 'humility',
      name: 'Modesty',
      description: "Humility: Recognizing one's insignificance in the larger context of the universe."
    }]
  }];
  const currentGroup = valueGroups[step];
  const toggleValue = (valueId: string, description: string) => {
    if (selectedValues.includes(valueId)) {
      setSelectedValues(selectedValues.filter(id => id !== valueId));
      // Remove from descriptions
      const newDescriptions = {
        ...valueDescriptions
      };
      delete newDescriptions[valueId];
      setValueDescriptions(newDescriptions);
    } else {
      // Only allow selecting up to 3 values
      if (selectedValues.length < 3) {
        setSelectedValues([...selectedValues, valueId]);
        setValueDescriptions({
          ...valueDescriptions,
          [valueId]: description
        });
      }
    }
  };
  const handleNextStep = () => {
    if (step < valueGroups.length - 1) {
      setStep(step + 1);
    } else {
      // Save the selected values and continue
      updateUserData({
        values: selectedValues,
        valueDescriptions: valueDescriptions
      });
      onContinue();
    }
  };
  const handlePrevStep = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };
  return <div data-id="element-392">
      <h2 className="text-2xl font-bold mb-6 text-center text-green-700" data-id="element-393">
        Your Core Principles
      </h2>
      <p className="text-gray-600 mb-6" data-id="element-394">
        Select up to 3 principles that resonate most deeply with you. These will
        help personalize your garden journey.
      </p>
      <h3 className="text-xl font-semibold mb-4 text-center text-green-600" data-id="element-395">
        {currentGroup.title}
      </h3>
      <div className="space-y-3 mb-6" data-id="element-396">
        {currentGroup.values.map(value => <button key={value.id} onClick={() => toggleValue(value.id, value.description)} className={`
              w-full flex items-center p-4 rounded-lg text-left transition-all
              ${selectedValues.includes(value.id) ? 'bg-green-100 border border-green-300' : 'bg-gray-50 border border-gray-200 hover:bg-gray-100'}
            `} data-id="element-397">
            <div className={`
              w-6 h-6 rounded-full mr-3 flex items-center justify-center
              ${selectedValues.includes(value.id) ? 'bg-green-500' : 'bg-gray-300'}
            `} data-id="element-398">
              {selectedValues.includes(value.id) ? <CheckIcon size={16} className="text-white" data-id="element-399" /> : <XIcon size={16} className="text-white" data-id="element-400" />}
            </div>
            <div data-id="element-401">
              <div className="font-medium" data-id="element-402">{value.name}</div>
              <div className="text-sm text-gray-600" data-id="element-403">{value.description}</div>
            </div>
          </button>)}
      </div>
      <div className="flex justify-between mb-4" data-id="element-404">
        <button onClick={handlePrevStep} disabled={step === 0} className={`
            px-4 py-2 rounded-lg
            ${step === 0 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}
          `} data-id="element-405">
          Previous
        </button>
        <div className="flex items-center" data-id="element-406">
          <span className="text-sm text-gray-500 mr-2" data-id="element-407">
            Selected: {selectedValues.length}/3
          </span>
          {[...Array(3)].map((_, i) => <div key={i} className={`
                w-3 h-3 rounded-full mx-0.5
                ${i < selectedValues.length ? 'bg-green-500' : 'bg-gray-200'}
              `} data-id="element-408" />)}
        </div>
        <button onClick={handleNextStep} className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700" data-id="element-409">
          {step < valueGroups.length - 1 ? 'Next' : 'Continue'}
        </button>
      </div>
      <div className="flex justify-center" data-id="element-410">
        {valueGroups.map((_, i) => <div key={i} className={`w-2 h-2 rounded-full mx-1 ${i === step ? 'bg-green-500' : 'bg-gray-300'}`} data-id="element-411" />)}
      </div>
    </div>;
};
export default SchwartzValuesTest;